export const property = {
  createProperty: {
    title: "Create property",
    of: "of",
  },
  sections: {
    details: "Property details",
    presentation: "Presentation",
    rooms: "Rooms",
    bookingSettings: "Booking settings",
    checkinSettings: "Check-in settings",
    extras: "Additional services",
  },

  menu: {
    name: "Name",
    location: "Location",
    logo: "Property logo",
    capacity: "Capacity",
    services: "Property services",
    description: "Description",

    roomTypes: "Room types",
    roomManagement: "Manage rooms",

    imagesRooms: "Room images",
    imagesProperty: "Property images",

    priceCurrency: "Price & currency",
    rates: "Rates",
    maintenance: "Property maintenance",

    scheduling: "Scheduling",

    sesHospedajes: "SES Lodgings",

    smartLocks: "Smart locks",
    experiences: "Experiences & services",
    chatbot: "AI ChatBot",
  },

  firstStep: {
    heading: "What’s your accommodation name?",
    subheading:
      "Attract guests with a catchy name that highlights the best of your place.",
    fieldLabel: "Your accommodation name",
    placeholder: "e.g., Paradise Beach",
    internalNameLabel: "Internal name",
    internalNamePlaceholder: "Internal reference name for the accommodation",
  },

  locationStep: {
    heading: "Where is your accommodation located?",
    subheading:
      "Provide the exact address so guests can easily find your place.",
    countryLabel: "Country",
    addressLabel: "Full address",
    addressPlaceholder: "e.g., 25 Sierpes Street, Seville",
    cityLabel: "City",
    cityPlaceholder: "e.g., Seville",
    zipLabel: "Postal code",
    zipPlaceholder: "e.g., 41004",
  },
  logoStep: {
    heading: "What is your accommodation's logo?",
    description:
      "Add your accommodation’s logo to look more professional and easier to identify. Allowed formats: PNG, JPG, SVG.",
    currentLogoTitle: "Current logo:",
    currentLogoAlt: "Current logo",
  },

  roomTypeStep: {
    heading: "Room information",
    description:
      "Give this room type a name, such as “Double Room” or “Family Suite”. It will help you recognise it at a glance in the calendar and on the booking channels. You can add your other room types later from your panel.",
    basicInfoTitle: "Basic information",
    nameLabel: "Name",
    namePlaceholder: "Custom name",
    occupancyTitle: "Room capacity",
    adultsLabel: "Adults",
    childrenLabel: "Children",
    infantsLabel: "Infants",
  },

  hotelRoomsStep: {
    heading: "Assign your property's physical rooms",
    description:
      "Create the actual rooms that will be available for bookings, such as “101 - Double Room” or “302 - Family Suite”. This will help you organize them on the calendar and manage availability with ease.",
    roomTypeLabel: "Room type",
    roomNameLabel: "Room name",
    roomNamePlaceholder: "Custom name",
    roomCodeLabel: "Room number or code",
    roomCodePlaceholder: "Optional",
  },

  capacityStep: {
    heading: "Share essential details about your place",
    description:
      "Specify the number of guests, bedrooms, and bathrooms. Note that bedrooms and bathrooms are considered private by default. If they are shared, you can update this later in your property details.",
    labels: {
      guests: "Guests",
      bedrooms: "Bedrooms",
      bathrooms: "Bathrooms",
    },
  },

  servicesStep: {
    heading: "Show guests what your place offers",
    subtitle:
      "Select the amenities available. You can add more later from the CMS.",
    services: {
      oven: "Oven & hob",
      pets_allowed: "Pets allowed",
      wifi: "Wi-Fi Internet",
      essentials: "Essentials",
      washing_machine: "Washing machine",
      air_conditioning: "Air conditioning",
      heating: "Central heating",
      parking: "Parking space",
      children_allowed: "Children allowed",
      refrigerator: "Refrigerator",
    },
  },

  priceStep: {
    heading: "What’s the nightly price?",
    subtitle:
      "Set the default price to create the first nightly rate, as well as your property's currency.",
    labels: {
      pricePerNight: "Nightly rate",
      currency: "Currency",
    },
    placeholders: {
      priceExample: "e.g., 100",
    },
    requiredAsterisk: "*",
  },

  descriptionStep: {
    heading: "How would you describe your place?",
    subtitle:
      "Attract guests with a short description of your property. Highlight available amenities and nearby points of interest.",
    placeholders: {
      example:
        "e.g., A stylish loft with city views—perfect for working, shopping, and soaking up the urban vibe.",
    },
    inspiration: "Need inspiration?",
    buttons: {
      useAI: "Use AI",
      generating: "Generating...",
    },
    errors: {
      generate:
        "There was an error generating the description. Please try again.",
    },
  },
  navigation: {
    save: "Save",
    next: "Next",
  },
  common: {
    success: "Success",
    error: "Error",
    cancel: "Cancel",
    save: "Save",
  },

  images: {
    heading: "Property images",
    subtitleHotel:
      "Add your room photos so they can be used on your direct web booking page.",
    subtitleApt:
      "Add your property photos so they can be used on your direct web booking page.",

    loadRoomsError: "Rooms could not be loaded",
    uploadSuccess: "Valid images uploaded successfully",
    uploadErrorGeneric: "Error uploading images",

    deleteConfirmMessage: "Are you sure you want to delete this image?",
    deleteConfirmHeader: "Confirmation",
    deleteAccept: "Delete",
    deleteReject: "Cancel",
    deleteSuccess: "Image deleted successfully",
    deleteError: "Error deleting image",

    setMainSuccess: "Main image updated",
    setMainError: "Error setting main image",

    invalidFilesTitle: "Invalid files",

    mainBadge: "Main",
  },

  dropzone: {
    prompt: "Drag images here or click to select",
    formatHint: "Formats: JPEG, PNG, GIF.",
    uploading: "Uploading images... {progress}%",
    invalidHeader: "Some files do not meet the requirements:",
    reasonType: "invalid type",
    reasonSize: "exceeds size limit",
    and: "and",
  },

  checkin: {
    heading: "When will the check-in links be sent?",
    subtitle:
      "Schedule the automatic sending of the check-in link to your guests.",

    options: {
      same_day: { amount: "Same day", text: "of check-in" },
      24: { amount: "24 hours", text: "before check-in" },
      48: { amount: "48 hours", text: "before check-in" },
      72: { amount: "72 hours", text: "before check-in" },
      disabled: { amount: "Disable", text: "I will send it manually" },
    },
    recommended: "Recommended",

    details: {
      sameDay: "6 AM same day",
      previousNight: "6 PM previous evening",
      corresponding: "6 PM corresponding",
    },

    confirm: {
      message: "Are you sure you want to change the check-in schedule?",
      header: "Confirm change",
      accept: "Change schedule",
      reject: "Cancel",
    },

    toasts: {
      loadError: {
        title: "Error",
        body: "Failed to load check-in settings.",
      },
      saveSuccess: {
        title: "Settings updated",
        body: "Process completed successfully",
      },
    },

    messages: {
      disabled: "Automatic sending is disabled. It must be sent manually.",
      same_day: "Check-ins will be sent at 6 AM on the same day of check-in",
      exactly24: "Check-ins will be sent at 6 PM on the evening before check-in",
      // If you don't have ICU plural, use a generic message
      daysBefore:
        "Check-ins will be sent at 6 PM, {days} day(s) before check-in",
    },
  },

  police: {
    header: "Register competent authority details",
    toasts: {
      saveSuccess: {
        title: "Action completed successfully",
        body: "Authority data has been saved successfully",
      },
    },
    errors: {
      lessorRequired: {
        title: "Error, lessor field",
        body: "Lessor code is required",
      },
      establishmentRequired: {
        title: "Error, establishment field",
        body: "Establishment code is required",
      },
    },
  },

  identity: {
    title: "Check-in store",
    subtitle: "When they check in, the guest gets a WhatsApp link with what they can add to their stay.",
    propertyLabel: "Property:",
    toggle: {
      title: "Enable identity verification",
      hint: "When enabled, guests will be required to verify their identity.",
      on: "ON",
      off: "OFF",
    },
    config: {
      whatTitle: "What do you ask the guest for?",
      whatHint: "Choose the photos they will have to take. At least one.",
      frontTitle: "Document front",
      frontDesc:
        "The side with the photo. This is the one compared with the selfie.",
      backTitle: "Document back",
      backDesc:
        "The reverse, where the address and the machine-readable zone usually are.",
      selfieTitle: "Live selfie",
      selfieDesc:
        "A photo of their face, taken on the spot with the camera.",
      compareOn: "The face on the document is compared with the selfie.",
      compareOff:
        "The photo is only stored: without both document and selfie there are no faces to compare.",
      requiredTitle: "And if the faces don't match…",
      noTitle: "They can continue",
      noDesc:
        "They finish the check-in anyway and you see it flagged in the panel.",
      yesTitle: "They cannot continue",
      yesDesc:
        "They won't be able to finish the check-in until it passes. They can try as many times as needed.",
      lockedHint:
        "To require it you need both the document front and the selfie.",
      summaryTitle: "The guest will see",
      needOne: "You must ask for at least one photo.",
      noStorage:
        "In this country we don't keep identity photos, only the result. Without face comparison no record will be left.",
      serviceDown:
        "If our comparison service fails, the guest is let through and you see it flagged: nobody is left at the door because of an outage on our side.",
    },
    toasts: {
      updated: "Identity verification settings updated successfully",
      updateError: "Error updating identity verification settings",
    },
  },

  guestReviews: {
    subtitle:
      "Enter the URL of the review site you want to configure so your guests can leave their feedback, either on Google or another review site you use. This helps improve the visibility and reputation of the accommodation.",
    enableTitle: "Enable Guest Reviews",
  },

  smartLocks: {
    title: "Smart Locks",
    subtitle:
      "Connect to compatible smart locks and forget about physical keys. Generate and send access codes automatically—no manual work, 100% secure.",
    propertyLabel: "Property",
    compatibleTitle: "Compatible Smart Locks",
    andManyMore: "and many more...",

    enableTitle: "Enable Smart Lock",
    enableHelp:
      "Send the access code to your guests once check-in is completed, right at the selected check-in time.",

    configTitle: "Configuration",
    configSubtitle: "Set your access code delivery preferences",

    deliveryLabel: "Access code delivery time",
    deliveryDesc:
      "Choose the time when the access code will be sent to your guest on the day of their reservation.",
    saveTimeBtn: "Save time",
    saveTimeLoading: "Saving...",

    accessTypeLabel: "Access code type",

    static: {
      title: "Static Code",
      desc: "Use the same code for all guests",
    },
    dynamic: {
      title: "Dynamic Code",
      desc: "Generate unique codes per reservation",
      soon: "Coming soon",
    },

    masterCodeLabel: "Master access code",
    masterCodePlaceholder: "Enter your master access code",
    saveCodeBtn: "Save code",
    saveCodeLoading: "Saving...",

    masterCodeHelp:
      "This code will be sent automatically to guests at the specified time on their check-in day.",

    dynamicInfo: {
      title: "Dynamic Access Codes",
      desc: "This premium feature will automatically generate unique access codes for each reservation, providing stronger security and a better guest experience. Available soon!",
    },

    support: {
      title: "Need help with the integration?",
      desc: "We integrate with all major smart-lock brands. Our technical team will help you configure your specific model.",
      cta: "Contact Support",
    },

    errors: {
      emptyKey: "Please enter an access key",
    },

    toasts: {
      updateSuccess: "Smart Lock settings updated successfully",
      updateError: "Error updating Smart Lock settings",
      timeSuccess: "Delivery time updated successfully",
      timeError: "Error updating delivery time",
      keySuccess: "Access key saved successfully",
      keyError: "Error saving the access key",
    },
  },

  services: {
    listMovedHelp: "Your service list is one for the whole account: write it once and tick which listings offer it.",
    listMovedLink: "Go to Services and experiences",
    title: "Experiences and Services",
    subtitle: "Manage your property's additional services.",
    enableTitle: "Enable experiences and services store",
    enableHelp:
      "Promote all additional experiences and services of your property. Guests receive a WhatsApp link at check-in.",
    storeType: {
      title: "Store type",
      internal: {
        title: "Internal store",
        desc: "Manage your services directly on the platform",
      },
      external: {
        title: "External store",
        desc: "Redirect guests to your external store",
      },
    },
    externalConfig: {
      title: "External store configuration",
      guestMessageLabel: "Message for guests",
      guestMessagePlaceholder:
        "E.g.: Discover our additional services and unique experiences in our online store...",
      guestMessageHelp:
        "Message shown to guests after check-in (max. 200 characters)",
      urlLabel: "Your external store URL",
      urlPlaceholder: "https://your-external-store.com",
      urlHelp: "Full URL where guests can book experiences and services",
      noteTitle: "Note:",
      noteText:
        "Guests will receive this personalized message with the link after completing check-in.",
    },
    addBtn: "Add",
    delete: {
      title: "Delete service?",
      text: "This action cannot be undone",
      confirm: "Delete",
      cancel: "Cancel",
    },
    toasts: {
      stateUpdated: "Service state updated",
      deleted: "Service successfully deleted",
      storeUpdated: "Experiences and services settings updated successfully",
      storeError: "Error updating experiences and services settings",
      configUpdated: "Store configuration updated successfully",
      configError: "Error updating store configuration",
    },
  },
  bot: {
    enableTitle: "Enable Virtual Assistant",
    enableHelp: "Automate guest support with quick WhatsApp replies.",
  },

  rooms: {
    heading: "Room types",
    subtitle: "Manage your property's room types.",
    addTypeCta: "Add room type",
    adultsCapacity: "Adults capacity: {count}",
    emptyList: "No rooms registered.",

    deleteConfirmHeader: "Confirm deletion",
    deleteConfirmMessage:
      "Are you sure you want to delete this room? This action cannot be undone.",
    deleteConfirmAccept: "Yes, delete",
    deleteConfirmReject: "Cancel",

    deleteSuccess: "Room deleted successfully.",
    deleteError: "Error deleting the room.",
  },

  roomMgmt: {
    remaining: "Available: {count}",
    heading: "Manage rooms",
    subtitle: "Assign individual rooms to the registered types.",
    totalRooms: "Total rooms: {count}",

    typeLabel: "Type",
    codeLabel: "Code",
    nameLabel: "Name",
    descLabel: "Description",

    selectPlaceholder: "Select",
    codePlaceholder: "Code",
    namePlaceholder: "Room name",
    descPlaceholder: "Room description",

    addBtn: "Add",

    planLimitTitle: "Limit reached",
    planLimitMsg:
      "You’ve reached the maximum number of rooms allowed by your plan. Please upgrade your subscription to add more.",

    loadTypesError: "Room types could not be loaded",
    loadRoomsError: "Existing rooms could not be loaded",

    saveSuccessTitle: "Success",
    saveSuccessMsg: "Rooms saved successfully",
    saveError: "Rooms could not be saved",

    deleteSuccess: "Room deleted successfully.",
    deleteError: "Error deleting the room.",
    formIncompleteError:
      "Please complete the room type and name for all rooms.",
  },

  rates: {
    heading: "Rates",
    subtitleHotel: "Manage the rates for your lodgings.",
    subtitleSingle: "Manage the rates for your lodging.",
    adultsCapacity: "Adults capacity: {count}",
    roomsCount: "Rooms {count}",
    singleUnit: "Accommodation unit",

    table: {
      name: "Name",
      occupancy: "Guests",
      priceTaxIncl: "Price (TAX INCLUDED)",
      actions: "Actions",
    },

    addRate: "Add rate",
    emptyList: "No rates registered.",

    deleteConfirmHeader: "Confirm deletion",
    deleteConfirmMessage:
      "Are you sure you want to delete this rate? This action cannot be undone.",
    deleteConfirmAccept: "Yes, delete",
    deleteConfirmReject: "Cancel",

    deleteSuccess: "Rate deleted successfully.",
    deleteError: "Error deleting the rate.",
  },
};

export default property;
